import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaArrowRight, 
  FaPalette, 
  FaCheck, 
  FaUsers,
  FaMobile,
  FaDesktop,
  FaRocket,
  FaShieldAlt,
  FaClock,
  FaStar,
  FaQuoteLeft,
  FaEye,
  FaPencilRuler,
  FaChartLine,
  FaBrush,
  FaLayerGroup,
  FaCode,
  FaCog
} from 'react-icons/fa';
import { SiFigma, SiSketch, SiAdobexd } from 'react-icons/si';
import { serviceSeoData, seoImage } from '../../config/pageSeoConfig';
import './UIUXDesign.scss';

const UIUXDesign: React.FC = () => {
  // SEO constants from config
  const seoTitle = serviceSeoData.uiUxDesign.title;
  const seoDescription = serviceSeoData.uiUxDesign.description;
  const seoKeywords = serviceSeoData.uiUxDesign.keywords;
  const seoUrl = serviceSeoData.uiUxDesign.url;
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "UI/UX Design Services",
    "description": seoDescription,
    "provider": {
      "@type": "Organization",
      "name": "App Developer Pro",
      "url": "https://appdeveloperpro.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://appdeveloperpro.com/images/team-at-work-app-developer-pro.jpg"
      }
    },
    "areaServed": "Worldwide",
    "serviceType": "UI/UX Design, User Experience, User Interface, Web Design, Mobile App Design, Prototyping, User Research",
    "url": seoUrl,
    "image": seoImage
  };
  // ...existing code for features, tools, benefits, process, testimonials...
  // Only one return statement below:
  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content="ui ux design, user experience agency, best ui ux company, figma design, web design, mobile app design, user research, prototyping, wireframing, usability testing, interface design, product design, digital design, 2025" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={seoUrl} />
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:image" content={seoImage} />
        <meta property="og:site_name" content="App Developer Pro" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />
        <meta name="twitter:site" content="@appdeveloperpro" />
        {/* Schema.org Markup */}
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>
      <div className="uiux-design">
        {/* Hero Section */}
        <section className="uiux-design__hero">
          <div className="uiux-design__hero-content">
            <motion.div
              className="uiux-design__hero-text"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="uiux-design__hero-title">
                Professional <span className="uiux-design__hero-highlight">UI/UX Design</span> Services
              </h1>
              <p className="uiux-design__hero-subtitle">
                Create exceptional user experiences that drive engagement and conversions. Our design experts craft beautiful, intuitive interfaces that users love.
              </p>
              <div className="uiux-design__hero-buttons">
                <Link to="/contact" className="uiux-design__hero-btn uiux-design__hero-btn--primary">
                  Start Design Project <FaArrowRight />
                </Link>
                <Link to="/portfolio" className="uiux-design__hero-btn uiux-design__hero-btn--secondary">
                  View Design Portfolio
                </Link>
              </div>
            </motion.div>
            <motion.div
              className="uiux-design__hero-visual"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="uiux-design__hero-design">
                <div className="uiux-design__hero-design-header">
                  <div className="uiux-design__hero-design-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <span className="uiux-design__hero-design-title">Design Artboard</span>
                </div>
                <div className="uiux-design__hero-design-canvas">
                  <div className="uiux-design__hero-design-artboard">
                    <div className="uiux-design__hero-design-nav">
                      <div className="uiux-design__hero-design-logo">
                        <FaBrush />
                      </div>
                      <div className="uiux-design__hero-design-menu">
                        <FaUsers />
                        <FaCog />
                      </div>
                    </div>
                    <div className="uiux-design__hero-design-content">
                      <div className="uiux-design__hero-design-heading"></div>
                      <div className="uiux-design__hero-design-text">
                        <div className="uiux-design__hero-design-line"></div>
                        <div className="uiux-design__hero-design-line"></div>
                        <div className="uiux-design__hero-design-line"></div>
                      </div>
                      <div className="uiux-design__hero-design-cta">
                        <div className="uiux-design__hero-design-button uiux-design__hero-design-button--primary"></div>
                        <div className="uiux-design__hero-design-button uiux-design__hero-design-button--secondary"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section className="uiux-design__features">
          <div className="container">
            <motion.div
              className="uiux-design__section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>Our UI/UX Design Services</h2>
              <p>Comprehensive design solutions for web and mobile applications</p>
            </motion.div>
            <div className="uiux-design__features-grid">
              <motion.div
                className="uiux-design__feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="uiux-design__feature-header">
                  <div className="uiux-design__feature-icon">
                    <FaEye />
                  </div>
                  <h3>User Research</h3>
                </div>
                <p>In-depth user research and analysis to understand your target audience and their needs.</p>
              </motion.div>
              <motion.div
                className="uiux-design__feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <div className="uiux-design__feature-header">
                  <div className="uiux-design__feature-icon">
                    <FaPencilRuler />
                  </div>
                  <h3>Wireframing</h3>
                </div>
                <p>Create detailed wireframes and prototypes to visualize the user journey and interface structure.</p>
              </motion.div>
              <motion.div
                className="uiux-design__feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="uiux-design__feature-header">
                  <div className="uiux-design__feature-icon">
                    <FaBrush />
                  </div>
                  <h3>Visual Design</h3>
                </div>
                <p>Beautiful, modern interface designs that align with your brand identity and user expectations.</p>
              </motion.div>
              <motion.div
                className="uiux-design__feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="uiux-design__feature-header">
                  <div className="uiux-design__feature-icon">
                    <FaMobile />
                  </div>
                  <h3>Mobile Design</h3>
                </div>
                <p>Responsive mobile app designs optimized for iOS and Android platforms.</p>
              </motion.div>
              <motion.div
                className="uiux-design__feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <div className="uiux-design__feature-header">
                  <div className="uiux-design__feature-icon">
                    <FaDesktop />
                  </div>
                  <h3>Web Design</h3>
                </div>
                <p>Pixel-perfect web designs that work seamlessly across all browsers and devices.</p>
              </motion.div>
              <motion.div
                className="uiux-design__feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                viewport={{ once: true }}
              >
                <div className="uiux-design__feature-header">
                  <div className="uiux-design__feature-icon">
                    <FaChartLine />
                  </div>
                  <h3>Usability Testing</h3>
                </div>
                <p>Comprehensive testing to ensure optimal user experience and interface usability.</p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Tools Section */}
        <section className="uiux-design__tools">
          <div className="container">
            <motion.div
              className="uiux-design__section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>Design Tools We Master</h2>
              <p>Industry-leading tools for professional UI/UX design</p>
            </motion.div>
            <div className="uiux-design__tools-grid">
              <motion.div
                className="uiux-design__tool-card"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <SiFigma className="uiux-design__tool-icon" />
                <h3>Figma</h3>
                <p>Collaborative design and prototyping</p>
              </motion.div>
              <motion.div
                className="uiux-design__tool-card"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <SiSketch className="uiux-design__tool-icon" />
                <h3>Sketch</h3>
                <p>Professional interface design</p>
              </motion.div>
              <motion.div
                className="uiux-design__tool-card"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <SiAdobexd className="uiux-design__tool-icon" />
                <h3>Adobe XD</h3>
                <p>Design and prototyping platform</p>
              </motion.div>
              <motion.div
                className="uiux-design__tool-card"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <FaLayerGroup className="uiux-design__tool-icon" />
                <h3>InVision</h3>
                <p>Collaborative design workflow</p>
              </motion.div>
              <motion.div
                className="uiux-design__tool-card"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <FaBrush className="uiux-design__tool-icon" />
                <h3>Adobe Illustrator</h3>
                <p>Vector graphics and icons</p>
              </motion.div>
            
            </div>
          </div>
        </section>

        {/* Benefits Section - SEO & User Engagement */}
        <section className="uiux-design__benefits">
          <div className="container">
            <motion.div
              className="uiux-design__section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>Why Professional UI/UX Design Matters for Your Business</h2>
              <p>Boost conversions, engagement, and customer satisfaction with exceptional design</p>
            </motion.div>
            <div className="uiux-design__benefits-grid">
              <motion.div
                className="uiux-design__benefit-card"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="uiux-design__benefit-header">
                  <div className="uiux-design__benefit-icon">
                    <FaChartLine />
                  </div>
                  <h3>200% Increase in Conversions</h3>
                </div>
                <p>Well-designed user interfaces can boost conversion rates by up to 200%, turning more visitors into customers.</p>
              </motion.div>
              <motion.div
                className="uiux-design__benefit-card"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <div className="uiux-design__benefit-header">
                  <div className="uiux-design__benefit-icon">
                    <FaUsers />
                  </div>
                  <h3>400% Better User Engagement</h3>
                </div>
                <p>Intuitive UX design keeps users engaged longer, reducing bounce rates and increasing time on site.</p>
              </motion.div>
              <motion.div
                className="uiux-design__benefit-card"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="uiux-design__benefit-header">
                  <div className="uiux-design__benefit-icon">
                    <FaStar />
                  </div>
                  <h3>Enhanced Brand Perception</h3>
                </div>
                <p>Professional design builds trust and credibility, positioning your brand as an industry leader.</p>
              </motion.div>
              <motion.div
                className="uiux-design__benefit-card"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="uiux-design__benefit-header">
                  <div className="uiux-design__benefit-icon">
                    <FaClock />
                  </div>
                  <h3>Reduced Development Costs</h3>
                </div>
                <p>Proper UX planning reduces costly revisions and speeds up development by 50%.</p>
              </motion.div>
              <motion.div
                className="uiux-design__benefit-card"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <div className="uiux-design__benefit-header">
                  <div className="uiux-design__benefit-icon">
                    <FaMobile />
                  </div>
                  <h3>Mobile-First Approach</h3>
                </div>
                <p>Designs optimized for mobile ensure seamless experiences across all devices and screen sizes.</p>
              </motion.div>
              <motion.div
                className="uiux-design__benefit-card"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                viewport={{ once: true }}
              >
                <div className="uiux-design__benefit-header">
                  <div className="uiux-design__benefit-icon">
                    <FaShieldAlt />
                  </div>
                  <h3>Accessibility Compliance</h3>
                </div>
                <p>WCAG-compliant designs ensure your product is accessible to all users, expanding your reach.</p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Design Process Section - Trust Builder */}
        <section className="uiux-design__process">
          <div className="container">
            <motion.div
              className="uiux-design__section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>Our Proven UI/UX Design Process</h2>
              <p>A systematic approach to creating exceptional user experiences</p>
            </motion.div>
            <div className="uiux-design__process-timeline">
              <motion.div
                className="uiux-design__process-step"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="uiux-design__process-number">01</div>
                <div className="uiux-design__process-content">
                  <h3>Discovery & Research</h3>
                  <p>We analyze your business goals, target audience, and competitors to create a solid foundation for design decisions.</p>
                  <ul>
                    <li>User persona development</li>
                    <li>Competitor analysis</li>
                    <li>Market research</li>
                    <li>Stakeholder interviews</li>
                  </ul>
                </div>
              </motion.div>
              <motion.div
                className="uiux-design__process-step"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <div className="uiux-design__process-number">02</div>
                <div className="uiux-design__process-content">
                  <h3>Information Architecture</h3>
                  <p>Structure content and features in a logical, intuitive way that guides users effortlessly through your product.</p>
                  <ul>
                    <li>Site mapping</li>
                    <li>User flow diagrams</li>
                    <li>Content strategy</li>
                    <li>Navigation design</li>
                  </ul>
                </div>
              </motion.div>
              <motion.div
                className="uiux-design__process-step"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="uiux-design__process-number">03</div>
                <div className="uiux-design__process-content">
                  <h3>Wireframing & Prototyping</h3>
                  <p>Create low and high-fidelity prototypes to visualize the user journey and test interactions early.</p>
                  <ul>
                    <li>Low-fidelity wireframes</li>
                    <li>Interactive prototypes</li>
                    <li>Clickable mockups</li>
                    <li>User testing</li>
                  </ul>
                </div>
              </motion.div>
              <motion.div
                className="uiux-design__process-step"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="uiux-design__process-number">04</div>
                <div className="uiux-design__process-content">
                  <h3>Visual Design</h3>
                  <p>Apply brand identity, color theory, and typography to create stunning, on-brand visual interfaces.</p>
                  <ul>
                    <li>UI component design</li>
                    <li>Design system creation</li>
                    <li>Brand integration</li>
                    <li>Responsive layouts</li>
                  </ul>
                </div>
              </motion.div>
              <motion.div
                className="uiux-design__process-step"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <div className="uiux-design__process-number">05</div>
                <div className="uiux-design__process-content">
                  <h3>Testing & Iteration</h3>
                  <p>Conduct usability testing, gather feedback, and refine the design for optimal user experience.</p>
                  <ul>
                    <li>A/B testing</li>
                    <li>User feedback sessions</li>
                    <li>Performance optimization</li>
                    <li>Final refinements</li>
                  </ul>
                </div>
              </motion.div>
              <motion.div
                className="uiux-design__process-step"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                viewport={{ once: true }}
              >
                <div className="uiux-design__process-number">06</div>
                <div className="uiux-design__process-content">
                  <h3>Development Handoff</h3>
                  <p>Provide developers with detailed specifications, assets, and documentation for seamless implementation.</p>
                  <ul>
                    <li>Design specifications</li>
                    <li>Asset export</li>
                    <li>Developer documentation</li>
                    <li>Ongoing support</li>
                  </ul>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Statistics Section - Social Proof */}
        <section className="uiux-design__stats">
          <div className="container">
            <div className="uiux-design__stats-grid">
              <motion.div
                className="uiux-design__stat-card"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <div className="uiux-design__stat-number">500+</div>
                <div className="uiux-design__stat-label">Projects Completed</div>
              </motion.div>
              <motion.div
                className="uiux-design__stat-card"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <div className="uiux-design__stat-number">98%</div>
                <div className="uiux-design__stat-label">Client Satisfaction</div>
              </motion.div>
              <motion.div
                className="uiux-design__stat-card"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="uiux-design__stat-number">150%</div>
                <div className="uiux-design__stat-label">Avg. Conversion Increase</div>
              </motion.div>
              <motion.div
                className="uiux-design__stat-card"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="uiux-design__stat-number">24/7</div>
                <div className="uiux-design__stat-label">Design Support</div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Testimonials Section - Social Proof */}
        <section className="uiux-design__testimonials">
          <div className="container">
            <motion.div
              className="uiux-design__section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>What Our Clients Say About Our UI/UX Design</h2>
              <p>Real feedback from businesses we've helped transform</p>
            </motion.div>
            <div className="uiux-design__testimonials-grid">
              <motion.div
                className="uiux-design__testimonial-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="uiux-design__testimonial-quote">
                  <FaQuoteLeft />
                </div>
                <div className="uiux-design__testimonial-stars">
                  <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                </div>
                <p>"The UI/UX design team transformed our app completely. User engagement increased by 300% and our app store ratings went from 3.2 to 4.8 stars!"</p>
                <div className="uiux-design__testimonial-author">
                  <strong>Sarah Johnson</strong>
                  <span>CEO, TechStart Inc.</span>
                </div>
              </motion.div>
              <motion.div
                className="uiux-design__testimonial-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <div className="uiux-design__testimonial-quote">
                  <FaQuoteLeft />
                </div>
                <div className="uiux-design__testimonial-stars">
                  <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                </div>
                <p>"Outstanding work! The new design is not only beautiful but also intuitive. Our conversion rate doubled within the first month of launch."</p>
                <div className="uiux-design__testimonial-author">
                  <strong>Michael Chen</strong>
                  <span>Founder, E-Commerce Pro</span>
                </div>
              </motion.div>
              <motion.div
                className="uiux-design__testimonial-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="uiux-design__testimonial-quote">
                  <FaQuoteLeft />
                </div>
                <div className="uiux-design__testimonial-stars">
                  <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                </div>
                <p>"Professional, creative, and results-driven. They understood our vision and delivered a design that exceeded our expectations."</p>
                <div className="uiux-design__testimonial-author">
                  <strong>Emily Rodriguez</strong>
                  <span>Product Manager, HealthTech</span>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* FAQ Section - SEO Boost */}
        <section className="uiux-design__faq">
          <div className="container">
            <motion.div
              className="uiux-design__section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>Frequently Asked Questions About UI/UX Design</h2>
              <p>Everything you need to know about professional UI/UX design services</p>
            </motion.div>
            <div className="uiux-design__faq-list">
              <motion.div
                className="uiux-design__faq-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <h3>What is the difference between UI and UX design?</h3>
                <p>UI (User Interface) design focuses on the visual elements and aesthetics of a product, including colors, typography, and layout. UX (User Experience) design focuses on the overall user journey, usability, and how users interact with the product. Both are essential for creating successful digital products.</p>
              </motion.div>
              <motion.div
                className="uiux-design__faq-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <h3>How long does a UI/UX design project typically take?</h3>
                <p>Project timelines vary based on complexity, but typically: Simple websites take 1-2 weeks, medium complexity apps take 2-4 weeks, and enterprise-level products take 4-8 weeks. We provide detailed timelines during the initial consultation.</p>
              </motion.div>
              <motion.div
                className="uiux-design__faq-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <h3>Do you offer mobile-first design?</h3>
                <p>Yes! We prioritize mobile-first design in all our projects. With over 60% of web traffic coming from mobile devices, we ensure your design looks perfect and functions seamlessly on smartphones, tablets, and desktops.</p>
              </motion.div>
              <motion.div
                className="uiux-design__faq-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <h3>What is your design revision policy?</h3>
                <p>We include up to 3 rounds of revisions in our standard packages to ensure you're completely satisfied with the final design. Additional revisions can be accommodated at reasonable rates.</p>
              </motion.div>
              <motion.div
                className="uiux-design__faq-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <h3>Do you provide design files and source files?</h3>
                <p>Absolutely! Upon project completion, you receive all design files including Figma/Sketch files, exported assets, design system documentation, and style guides for your development team.</p>
              </motion.div>
              <motion.div
                className="uiux-design__faq-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                viewport={{ once: true }}
              >
                <h3>Can you redesign an existing product?</h3>
                <p>Yes! We specialize in both new product design and redesigning existing products. We analyze your current design, identify pain points, and create an improved version that enhances user experience and achieves your business goals.</p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="uiux-design__cta">
          <div className="container">
            <motion.div
              className="uiux-design__cta-content"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>Ready to Transform Your User Experience?</h2>
              <p>Let's create beautiful, intuitive designs that your users will love and that drive real business results.</p>
              <div className="uiux-design__cta-buttons">
                <Link to="/contact" className="uiux-design__cta-btn uiux-design__cta-btn--primary">
                  Start Your Project <FaArrowRight />
                </Link>
                <Link to="/portfolio" className="uiux-design__cta-btn uiux-design__cta-btn--secondary">
                  View Our Work
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default UIUXDesign;
